import React from "react";
import logger from "../../../../../utils/LoggerFrontend.js";
import { ButtonOutlinedAnchor } from "../../Components/Buttons.jsx";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import CampaignCreatePerformanceEditor from "../../campaign-create-performance-editor/CampaignCreatePerformanceEditor.jsx";
import ChannelDataEditor from "../../Campaign__Create_ChannelDataEditor.jsx";
import { channelsByShortName } from "../../../../../data/app__channels.js";
import { useTranslation } from "react-i18next";

import * as SC from "./PostEditorLinkedInFeed.styled.js";
import useMediaAssets from "../hooks/useMediaAssets.js";
import { Tooltip } from "../../../../../Components/Overlays";
import TemplatePostPreview from "../../TemplatePost/TemplatePost__Preview/TemplatePostPreview.jsx";

export const PageHeader = ({
  pageLogo,
  pageName,
  pageId,
  pageNameColor,
  sponsoredMessageColor,
  sponsoredLogo,
  campaignLanguage
}) => {
  const { t } = useTranslation();

  logger.debug("PostEditorLinkedInFeed:PageHeader:render", "renders", { pageId });
  return (
    <SC.PageHeaderWrapper>
      <SC.PageLogo src={pageLogo} alt="" />
      <div>
        <SC.PageName color={pageNameColor}>{pageName}</SC.PageName>
        <SC.SponsoredMessage color={sponsoredMessageColor}>
          {t("Sponsored", { lng: campaignLanguage })}
          {sponsoredLogo && (
            <Tooltip label={t("Playout as sponsored advertisement")} bgColor="#1C1928">
              <SC.SponsoredLogo src="/images/sponsored_icon.svg" alt="" />
            </Tooltip>
          )}
        </SC.SponsoredMessage>
      </div>
    </SC.PageHeaderWrapper>
  );
};

const PostEditorLinkedInFeed = ({
  orgaId,
  postData,
  channelData,
  updatePost,
  updateChannelData,
  mobile,
  adsetId,
  editable,
  channel,
  loading,
  selectedCreativeIndexToEdit,
  setSelectedCreativeIndexToEdit,
  imageDimensions,
  campaignLanguage
}) => {
  logger.debug("PostEditorLinkedInFeed:render", "render:", {
    postData,
    channelData,
    mobile,
    editable,
    adsetId,
    imageDimensions
  });

  const mediaAssets = useMediaAssets(postData);
  const { t } = useTranslation();

  const templatePreviewSizes = {
    minHeight: SC.LINKEDINFEED_IMAGE_SIZE_SPEC[mobile ? "mobile" : "web"].minHeight,
    maxHeight: SC.LINKEDINFEED_IMAGE_SIZE_SPEC[mobile ? "mobile" : "web"].maxHeight,
    width: SC.LINKEDINFEED_IMAGE_SIZE_SPEC[mobile ? "mobile" : "web"].pictureWidth,
    minAspect: channelsByShortName["linkedin-feed"].minAspect,
    maxAspect: channelsByShortName["linkedin-feed"].maxAspect
  };

  return (
    <SC.PostEditorLinkedInFeedWrapper mobile={mobile}>
      {mobile && <SC.MobileTop src="/images/mobile-top.png" alt="" />}
      <div style={{ display: "flex" }}>
        {mobile && <SC.MobileSide src="/images/mobile-left.png" alt="" />}
        <SC.LinkedInFeedEditorWrapper mobile={mobile}>
          <SC.LinkedInFeedEditor mobile={mobile}>
            <SC.Header>
              <PageHeader
                pageLogo="/images/linkedin.png"
                pageName="LinkedIn Feed"
                pageId={channelData?.pageId}
                campaignLanguage={campaignLanguage}
                sponsoredLogo
              />
            </SC.Header>
            <SC.EditorWrapper>
              <CampaignCreateTeaserEditor
                channel="linkedin-feed"
                updatePost={updatePost}
                teaserText={postData.teaserText}
                editable={editable}
                teaserTextStyle={SC.teaserTextStyle}
                smallButtons={mobile}
                disableEmojis={true}
                limit={600}
              />
            </SC.EditorWrapper>
            <SC.PhotoEditorWrapper>
              <TemplatePostPreview
                orgaId={orgaId}
                creatives={postData.creatives}
                creativeAssets={postData.creativeAssets}
                selectedMediaAssetIndexToEdit={selectedCreativeIndexToEdit}
                mediaAssets={mediaAssets}
                updatePost={updatePost}
                channel={channel}
                editable={editable}
                templatePreviewSizes={templatePreviewSizes}
              />

              <Tooltip label={t("LinkedInFeed display image")} bgColor="#1C1928">
                <SC.LinkedInFeedInfoLogo src="/images/facebook_info_icon.svg" alt="" />
              </Tooltip>
            </SC.PhotoEditorWrapper>
            <SC.FooterWrapper>
              <SC.FooterTextSections>
                <ChannelDataEditor
                  tooltip={t("Displayed link")}
                  updateChannelData={updateChannelData}
                  value={channelData.displayLink}
                  dataKey="displayLink"
                  textStyle={SC.displayLinkStyle}
                  limit={channelsByShortName["linkedin-feed"].limits.displayLink}
                  label={t("Displayed link")}
                  bannedCharaters={["!", "?"]}
                  editable={editable}
                  urlValue={channelData.url}
                />

                <ChannelDataEditor
                  tooltip={t("Job Title")}
                  updateChannelData={updateChannelData}
                  value={channelData.title}
                  dataKey="title"
                  textStyle={SC.titleStyle}
                  limit={channelsByShortName["linkedin-feed"].limits.title}
                  label={t("Job Title")}
                  editable={editable}
                />

                <ChannelDataEditor
                  tooltip={t("Subtitle")}
                  updateChannelData={updateChannelData}
                  value={channelData.subHeadline}
                  dataKey="subHeadline"
                  textStyle={SC.subHeadlineStyle}
                  limit={channelsByShortName["linkedin-feed"].limits.subHeadline}
                  label={t("Subtitle")}
                  editable={editable}
                />
              </SC.FooterTextSections>
              <ButtonOutlinedAnchor target="_blank" href={channelData?.url || "#"}>
                {t("Read more", { lng: campaignLanguage })}
              </ButtonOutlinedAnchor>
            </SC.FooterWrapper>
            {!loading && (
              <CampaignCreatePerformanceEditor
                creatives={postData.creatives}
                creativeAssets={postData.creativeAssets}
                logos={postData.logos || postData.creatives.map(() => "")}
                editable={editable}
                mediaAssets={mediaAssets}
                channel={channel}
                updatePost={updatePost}
                padding="0 11px 16px"
                selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
                setSelectedCreativeIndexToEdit={setSelectedCreativeIndexToEdit}
              />
            )}
          </SC.LinkedInFeedEditor>
        </SC.LinkedInFeedEditorWrapper>
        {mobile && <SC.MobileSide src="/images/mobile-right.png" alt="" />}
      </div>
      {mobile && <SC.MobileBottom src="/images/mobile-bottom.png" alt="" />}
    </SC.PostEditorLinkedInFeedWrapper>
  );
};

export default PostEditorLinkedInFeed;
